<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'settings.numberings',
    path: 'numberings',
    component: 'Numberings',
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Numberings',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const table = ref(null);

  const $refs = {
    get table() {
      return table.value;
    },
  };

  const openNumbering = (...args) => {
    const [oid] = args;

    return $imodal.value
      .show({
        name: 'Numbering',
        props: { oid },
      })
      .then((res) => loadData('items'));
  };

  const items = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_3343864469950044_editable = computed(() => false);
  const b_3343864469950044_columns = computed(() => [
    {
      name: 'key',
      header: $fn.value.pgettext('Numbering', 'Key'),
      width: '120px',
      formatter: {
        name: 'template',
        value:
          '<div>\n\
  <a class="text-nowrap"\n\
     href="#"\n\
     @click.prevent="globs.openNumbering(row.id)"\n\
  >\n\
    {{ value }}\n\
  </a>\n\
</div>\n\
',
      },
    },
    {
      name: 'resource_type',
      header: $fn.value.pgettext('Numbering', 'Resource Type'),
      width: '120px',
    },
    {
      name: 'description',
      header: $fn.value.pgettext('Numbering', 'Description'),
      width: '160px',
    },
    {
      name: 'format',
      header: $fn.value.pgettext('Numbering', 'Format'),
      width: '260px',
    },
    {
      name: 'increment',
      type: 'number',
      header: $fn.value.pgettext('Numbering', 'Increment'),
      width: '110px',
    },
    {
      name: 'next_value',
      type: 'number',
      header: $fn.value.pgettext('Numbering', 'Next value'),
      width: '110px',
    },
    {
      name: 'next_number',
      type: 'number',
      header: $fn.value.pgettext('Numbering', 'Next Number'),
      width: '110px',
    },
  ]);

  const $data = computed(() => ({
    items: items.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_3343864469950044_editable: b_3343864469950044_editable.value,
    b_3343864469950044_columns: b_3343864469950044_columns.value,
  }));

  const $$load_items = async () => {
    return $fn.value.fetch('list_numbering');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'items' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_3343864469950044_editable', nodeId: '3343864469950044' },
      { name: 'b_3343864469950044_columns', nodeId: '3343864469950044' },
    ],
    loaders: { items: $$load_items },
    ux: {},
    dataRefs: {
      items,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_3343864469950044_editable,
      b_3343864469950044_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_8d23qwk7z_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_6705964765214521_showModal_2dmhr070n_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Numbering',
    };
  };
  const act_3343864469950044_script_y14jpywa7_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('numbering').upsert({
        ...$event.data.row,
        start: $event.data.row.lastvalue,
      })
    );
  };
  const act_3343864469950044_script_y14jpywa7_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3343864469950044_script_y14jpywa7_value_callable($event);
      },
    };
  };
  const act_3343864469950044_script_2bl4haz17_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('numbering').delete($event.data.row)
    );
  };
  const act_3343864469950044_script_2bl4haz17_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3343864469950044_script_2bl4haz17_value_callable($event);
      },
    };
  };

  const act_7315092382398562_closeModal_8d23qwk7z =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_8d23qwk7z_getActionArgs,
    });
  const inst_evh_7315092382398562_close_cf1ecanqy =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_8d23qwk7z,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_cf1ecanqy],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_6jlorfo8m =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_yjcm5p6ew =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_6jlorfo8m,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_yjcm5p6ew],
    event: 'reload',
  });
  const act_8320016629450276_reloadComponentData_g19tr5wmc =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_8320016629450276_clickPreventStop_xemxdcyyz =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_reloadComponentData_g19tr5wmc,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_8320016629450276_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8320016629450276_clickPreventStop_xemxdcyyz],
      event: 'click.prevent.stop',
    });
  const act_6705964765214521_showModal_2dmhr070n =
    new actionTypes.ShowModalAction({
      actionArgs: act_6705964765214521_showModal_2dmhr070n_getActionArgs,
    });
  const inst_evh_6705964765214521_clickPreventStop_kiuesuyb6 =
    new actionTypes.EventHandlerImpl({
      action: act_6705964765214521_showModal_2dmhr070n,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_6705964765214521_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6705964765214521_clickPreventStop_kiuesuyb6],
      event: 'click.prevent.stop',
    });
  const act_3343864469950044_script_y14jpywa7 = new actionTypes.ScriptAction({
    actionArgs: act_3343864469950044_script_y14jpywa7_getActionArgs,
  });
  const inst_evh_3343864469950044_save_h6px737cl =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_script_y14jpywa7,
      event: 'save',
      displayName: 'script',
    });
  const inst_evh_3343864469950044_save = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3343864469950044_save_h6px737cl],
    event: 'save',
  });
  const act_3343864469950044_script_2bl4haz17 = new actionTypes.ScriptAction({
    actionArgs: act_3343864469950044_script_2bl4haz17_getActionArgs,
  });
  const inst_evh_3343864469950044_delete_m78d2soc4 =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_script_2bl4haz17,
      event: 'delete',
      displayName: 'script',
    });
  const inst_evh_3343864469950044_delete = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3343864469950044_delete_m78d2soc4],
    event: 'delete',
  });

  const actions = {
    inst_evh_7315092382398562_close_cf1ecanqy,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_yjcm5p6ew,
    inst_evh_7315092382398562_reload,
    inst_evh_8320016629450276_clickPreventStop_xemxdcyyz,
    reload: inst_evh_8320016629450276_clickPreventStop_xemxdcyyz,
    inst_evh_8320016629450276_clickPreventStop,
    inst_evh_6705964765214521_clickPreventStop_kiuesuyb6,
    inst_evh_6705964765214521_clickPreventStop,
    inst_evh_3343864469950044_save_h6px737cl,
    inst_evh_3343864469950044_save,
    inst_evh_3343864469950044_delete_m78d2soc4,
    inst_evh_3343864469950044_delete,
  };

  const evh_7315092382398562_close_cf1ecanqy = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_cf1ecanqy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_yjcm5p6ew = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_yjcm5p6ew.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop_xemxdcyyz = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop_xemxdcyyz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_clickPreventStop_kiuesuyb6 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6705964765214521_clickPreventStop_kiuesuyb6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_save_h6px737cl = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_save_h6px737cl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_save = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_save.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_delete_m78d2soc4 = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_delete_m78d2soc4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_delete = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_delete.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Numberings"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="overflow-hidden d-flex flex-column h-100 p-3" v-if="loaded">
      <ItpBox alignItems="center" :gap="2" class="mb-3">
        <strong>
          {{ pgettext('Numberings', 'Numberings') }}
        </strong>
        <ItpButton
          icon="fa-sync-alt"
          :text="gettext('Reload')"
          variant="light"
          size="sm"
          :spinning="actions.reload.isRunning"
          @click.prevent.stop="
            evh_8320016629450276_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          :text="pgettext('Numberings', 'Add numbering...')"
          variant="light"
          size="sm"
          @click.prevent.stop="
            evh_6705964765214521_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </ItpBox>
      <ItpDataTable
        tableId="Settings.Numberings"
        :editable="b_3343864469950044_editable"
        no-create-row
        no-delete-row
        :value="_.orderBy(items, ['key', 'description'])"
        :globs="{ openNumbering }"
        :columns="b_3343864469950044_columns"
        class="itp-datatable--flex-fill"
        ref="table"
        @save="evh_3343864469950044_save($event, {})"
        @delete="evh_3343864469950044_delete($event, {})"
      >
      </ItpDataTable>
    </div>
  </ItpPage>
</template>
